import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../../components/BackendErrorMessage';
import Unidade from '../../../../model/Unidade';
import useUserStore from '../../../../stores/userStore';
import { ProcessoViewModel } from '../../../../viewmodel/ProcessoViewModel';
import ContaServicoViewModel from '../../../../viewmodel/ContaServicoViewModel';
import EtapaEditais from './EtapaEditais';
import EtapaInformacoes from './EtapaInformacoes';
import EtapaContaServico from './EtapaContaServico';
import EtapaConfiguracoesAvancadas from './EtapaConfiguracoesAvancadas';
import { FormData, Edital } from './types';
import { modalStyles } from './styles';
import AcessoCandidatoConfiguracaoViewModel from '../../../../viewmodel/AcessoCandidatoConfiguracaoViewModel';
import { BsQuestionCircle, BsChevronDown, BsChevronUp } from 'react-icons/bs';

interface CriarProcessoModalProps {
  unidades: Unidade[];
  onClose: () => void;
  onProcessoCriado: () => void;
}

const CriarProcessoModal: React.FC<CriarProcessoModalProps> = ({ unidades, onClose, onProcessoCriado }) => {
  const [etapa, setEtapa] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    identificador: '',
    responsavel: '',
    unidadeId: '',
    categoria: '',
    editais: [{ nome: '', hasChamadas: true }],
    contasServicoIds: [],
    usarConfiguracaoGlobalAcessoCandidato: false,
    janelaLiberacaoHoras: 3,
    envioFormularioRecursoPorEmail: true,
    prazoInterposicaoRecursoValor: 3,
    prazoInterposicaoRecursoUnidade: 'HORAS',
    envioAutomaticoResultadoPorEtapaHabilitado: false,
  });
  const [contasServico, setContasServico] = useState<any[]>([]);
  const [contasSelecionadas, setContasSelecionadas] = useState<any[]>([]);
  const [permissoesPorConta, setPermissoesPorConta] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUnidades, setFilteredUnidades] = useState<Unidade[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [configGlobal, setConfigGlobal] = useState<{ janelaLiberacaoHoras: number; envioFormularioRecursoPorEmail: boolean } | null>(null);

  const contasIntegracao = contasServico.filter(c =>
    c.perfilNome?.startsWith('INTEGRACAO_CANDIDATOS')
  );

  useEffect(() => {
    setIsVisible(true);
    carregarContasServico();
    carregarConfiguracaoGlobalAcessoCandidato();
  }, []);

  const carregarConfiguracaoGlobalAcessoCandidato = async () => {
    try {
      const configGlobal = await AcessoCandidatoConfiguracaoViewModel.buscar();
      if (!configGlobal) return;
      setConfigGlobal({
        janelaLiberacaoHoras: configGlobal.janelaLiberacaoHoras,
        envioFormularioRecursoPorEmail: configGlobal.envioFormularioRecursoPorEmail
      });
      setFormData(prev => ({
        ...prev,
        janelaLiberacaoHoras: configGlobal.janelaLiberacaoHoras ?? prev.janelaLiberacaoHoras,
        envioFormularioRecursoPorEmail: configGlobal.envioFormularioRecursoPorEmail ?? prev.envioFormularioRecursoPorEmail
      }));
    } catch (error) {
      console.error('Erro ao carregar configuração global de acesso do candidato:', error);
    }
  };

  const carregarContasServico = async () => {
    try {
      const response = await ContaServicoViewModel.getContasAtivasEAprovadas(0, 1000);
      setContasServico(response.content || []);
    } catch (error) {
      console.error('Erro ao carregar contas de serviço:', error);
    }
  };

  const handleToggleConta = async (conta: any) => {
    const jaSelecionada = contasSelecionadas.some(c => c.clientId === conta.clientId);
    if (jaSelecionada) {
      const novas = contasSelecionadas.filter(c => c.clientId !== conta.clientId);
      setContasSelecionadas(novas);
      setFormData(prev => ({ ...prev, contasServicoIds: novas.map(c => c.clientId) }));
    } else {
      const novas = [...contasSelecionadas, conta];
      setContasSelecionadas(novas);
      setFormData(prev => ({ ...prev, contasServicoIds: novas.map(c => c.clientId) }));
      if (!permissoesPorConta[conta.clientId]) {
        try {
          const permissoes = await ContaServicoViewModel.getPermissoesCustomizadas(conta.clientId);
          setPermissoesPorConta(prev => ({ ...prev, [conta.clientId]: permissoes }));
        } catch (error) {
          console.error('Erro ao carregar permissões:', error);
        }
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'envioFormularioRecursoPorEmail' || field === 'envioAutomaticoResultadoPorEtapaHabilitado') {
      setFormData(prev => ({ ...prev, [field]: value === 'true' }));
      return;
    }
    if (field === 'janelaLiberacaoHoras' || field === 'prazoInterposicaoRecursoValor') {
      setFormData(prev => ({ ...prev, [field]: Number(value) }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleGlobal = (usarGlobal: boolean) => {
    setFormData(prev => ({ ...prev, usarConfiguracaoGlobalAcessoCandidato: usarGlobal }));
  };

  const handleEditalChange = (index: number, field: keyof Edital, value: string) => {
    setFormData(prev => ({
      ...prev,
      editais: prev.editais.map((edital, i) =>
        i === index ? {
          ...edital,
          [field]: field === 'hasChamadas' ? value === 'true' : value
        } : edital
      )
    }));
  };

  const addEdital = () => {
    setFormData(prev => ({
      ...prev,
      editais: [...prev.editais, { nome: '', hasChamadas: true }]
    }));
  };

  const removeEdital = (index: number) => {
    setFormData(prev => ({
      ...prev,
      editais: prev.editais.length > 1 ? prev.editais.filter((_, i) => i !== index) : prev.editais
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === '') {
      setFilteredUnidades([]);
      setIsDropdownOpen(false);
    } else {
      const filtered = unidades.filter(unidade =>
        unidade.nome.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredUnidades(filtered);
      setIsDropdownOpen(true);
    }
  };

  const handleSelectUnidade = (unidade: Unidade) => {
    setSearchTerm(unidade.nome);
    setFormData(prev => ({ ...prev, unidadeId: unidade.id || '' }));
    setIsDropdownOpen(false);
  };

  const preencherResponsavel = () => {
    const { usuario } = useUserStore.getState();
    if (usuario?.nome) {
      setFormData(prev => ({ ...prev, responsavel: usuario.nome || '' }));
    } else {
      toast.warning('Nome do usuário não encontrado');
    }
  };

  const gerarSugestaoIdentificador = () => {
    if (!formData.categoria) return '';
    const agora = new Date();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const ano = String(agora.getFullYear()).slice(-2);
    return `${formData.categoria.toLowerCase()}-${mes}-${ano}`;
  };

  const aplicarSugestao = () => {
    const sugestao = gerarSugestaoIdentificador();
    if (sugestao) {
      setFormData(prev => ({ ...prev, identificador: sugestao }));
    }
  };

  const proximaEtapa = () => {
    if (etapa === 1) {
      if (!formData.categoria) {
        toast.error('Selecione a categoria dos editais');
        return;
      }
      if (!formData.editais.some(e => e.nome)) {
        toast.error('Preencha pelo menos um edital');
        return;
      }
    }
    if (etapa === 2) {
      if (!formData.identificador || !formData.responsavel || !formData.unidadeId) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
    }
    setSlideDirection('right');
    setEtapa(prev => prev + 1);
  };

  const etapaAnterior = () => {
    setSlideDirection('left');
    setEtapa(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!formData.usarConfiguracaoGlobalAcessoCandidato) {
      if (!Number.isFinite(formData.janelaLiberacaoHoras) || formData.janelaLiberacaoHoras <= 0) {
        toast.error('Informe uma janela de liberação válida (horas).');
        return;
      }
      if (!Number.isFinite(formData.prazoInterposicaoRecursoValor) || formData.prazoInterposicaoRecursoValor <= 0) {
        toast.error('Informe um prazo de recurso válido.');
        return;
      }
    }

    setLoading(true);
    try {
      await ProcessoViewModel.criarProcesso({
        nome: formData.identificador.trim(),
        unidade: formData.unidadeId,
        responsavel: formData.responsavel.trim(),
        identificador: formData.identificador.trim(),
        editais: formData.editais.filter(e => e.nome).map(e => ({
          nome: e.nome.trim(),
          categoria: formData.categoria,
          hasChamadas: e.hasChamadas,
          url: e.url || undefined
        })),
        contasServicoIds: formData.contasServicoIds.length > 0 ? formData.contasServicoIds : undefined,
        configuracaoAcessoCandidato: {
          modo: formData.usarConfiguracaoGlobalAcessoCandidato ? 'GLOBAL' : 'PROCESSO',
          janelaLiberacaoHoras: formData.usarConfiguracaoGlobalAcessoCandidato ? undefined : formData.janelaLiberacaoHoras,
          envioFormularioRecursoPorEmail: formData.usarConfiguracaoGlobalAcessoCandidato ? undefined : formData.envioFormularioRecursoPorEmail,
          prazoInterposicaoRecursoValor: formData.usarConfiguracaoGlobalAcessoCandidato ? undefined : formData.prazoInterposicaoRecursoValor,
          prazoInterposicaoRecursoUnidade: formData.usarConfiguracaoGlobalAcessoCandidato ? undefined : formData.prazoInterposicaoRecursoUnidade,
          envioAutomaticoResultadoPorEtapaHabilitado: formData.envioAutomaticoResultadoPorEtapaHabilitado
        }
      });
      toast.success('Processo criado com sucesso!');
      onProcessoCriado();
      onClose();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const helpTexts: Record<number, { title: string; text: string }> = {
    1: { title: 'Editais e Categoria', text: 'Selecione a categoria do processo (define a composição das bancas) e adicione os editais vinculados. Cada edital pode ter chamadas independentes.' },
    2: { title: 'Informações do Processo', text: 'Preencha o identificador único, o responsável e a unidade. A unidade determina quais contas de serviço estarão disponíveis.' },
    3: { title: 'Contas de Serviço', text: 'Vincule contas de serviço para integração externa (envio de candidatos, fotos, etc.). Esta etapa é opcional.' },
    4: { title: 'Configurações Avançadas', text: 'Defina a janela de liberação de acesso para candidatos, envio de formulário de recurso por email e prazo de interposição de recurso.' },
  };

  const renderEtapa = () => {
    const baseProps = { formData, onInputChange: handleInputChange, slideDirection };

    switch (etapa) {
      case 1:
        return (
          <EtapaEditais
            {...baseProps}
            onEditalChange={handleEditalChange}
            onAddEdital={addEdital}
            onRemoveEdital={removeEdital}
          />
        );
      case 2:
        return (
          <EtapaInformacoes
            {...baseProps}
            unidades={unidades}
            searchTerm={searchTerm}
            filteredUnidades={filteredUnidades}
            isDropdownOpen={isDropdownOpen}
            onSearchChange={handleSearchChange}
            onSelectUnidade={handleSelectUnidade}
            onPreencherResponsavel={preencherResponsavel}
            onAplicarSugestao={aplicarSugestao}
            gerarSugestao={gerarSugestaoIdentificador}
          />
        );
      case 3:
        return (
          <EtapaContaServico
            {...baseProps}
            unidades={unidades}
            contas={contasIntegracao}
            contasSelecionadas={contasSelecionadas}
            permissoesPorConta={permissoesPorConta}
            onToggleConta={handleToggleConta}
          />
        );
      case 4:
        return (
          <EtapaConfiguracoesAvancadas
            {...baseProps}
            onToggleGlobal={handleToggleGlobal}
            configGlobal={configGlobal}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <style>{modalStyles}</style>
      <div className="modal modal-lg fade show" style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.5)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out'
      }}>
        <div className="modal-dialog" style={{
          transform: `scale(${isVisible ? 1 : 0.9}) translateY(${isVisible ? '0' : '20px'})`,
          transition: 'all 0.2s ease-in-out'
        }}>
          <div className="modal-content" style={{ borderRadius: '16px', border: 'none', overflow: 'hidden' }}>
            <div className="modal-header" style={{ 
              backgroundColor: 'white', 
              borderBottom: '1px solid #f0f0f0',
              padding: '24px 32px'
            }}>
              <div>
                <h5 className="modal-title fw-bold mb-1" style={{ fontSize: '20px' }}>Criar Processo</h5>
                <small className="text-muted" style={{ fontSize: '13px' }}>Etapa {etapa} de 4</small>
              </div>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div>
              <div className="modal-body" style={{ padding: '32px', minHeight: '320px', backgroundColor: '#fafafa' }}>
                <div className="mb-4">
                  <div className="progress" style={{ height: '3px', backgroundColor: '#e9ecef', borderRadius: '3px' }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: `${(etapa / 4) * 100}%`,
                        transition: 'width 0.3s ease',
                        backgroundColor: '#0d6efd',
                        borderRadius: '3px'
                      }}
                    ></div>
                  </div>
                </div>
                <div
                  onClick={() => setShowHelp(!showHelp)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#6c757d',
                    marginBottom: showHelp ? '0' : '16px',
                    userSelect: 'none',
                  }}
                >
                  <BsQuestionCircle />
                  <span>Ajuda</span>
                  {showHelp ? <BsChevronUp size={12} /> : <BsChevronDown size={12} />}
                </div>
                {showHelp && (
                  <div style={{
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #bee5eb',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#0c5460',
                    lineHeight: '1.5',
                  }}>
                    <strong>{helpTexts[etapa]?.title}:</strong> {helpTexts[etapa]?.text}
                  </div>
                )}
                {renderEtapa()}
              </div>
              <div className="modal-footer" style={{ 
                borderTop: '1px solid #f0f0f0', 
                padding: '20px 32px',
                backgroundColor: 'white'
              }}>
                <button 
                  type="button" 
                  className="btn btn-light" 
                  onClick={onClose}
                  style={{ borderRadius: '8px', padding: '8px 20px' }}
                >
                  Cancelar
                </button>
                <div className="ms-auto d-flex gap-2">
                  {etapa > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={etapaAnterior}
                      style={{ borderRadius: '8px', padding: '8px 20px' }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Anterior
                    </button>
                  )}
                  {etapa < 4 ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={proximaEtapa}
                      disabled={false}
                      style={{ borderRadius: '8px', padding: '8px 24px' }}
                    >
                      Próximo
                      <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={loading}
                      onClick={handleSubmit}
                      style={{ borderRadius: '8px', padding: '8px 24px' }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Criando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>
                          Criar Processo
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CriarProcessoModal;
